import { z } from 'zod';
import { ANZEIGE_LABEL } from './ausspielung';
import type { WerbemittelArt, WerbemittelKurz } from './werbemittel';
import type { NichtBewerbbarGrund, ProduktgruppeArt } from './produktgruppen';

/**
 * Retail Media RM-2: Kampagnen, Anzeigengruppen und beworbene Produkte.
 *
 * Kampagne      gehört zu einem Werbekonto; Gesamt- und Tagesbudget in Cent, Laufzeit, Status
 * Anzeigengruppe Gebot je Klick, Strategie und die Platzierungen, auf denen sie bietet
 * Anzeige-Produkt ein Produkt des Händlers (im Marktplatz optional eine Variante = Angebot)
 *
 * Gespeichert wird der Status, den der Werbetreibende setzt (entwurf, aktiv, pausiert, beendet). Ob eine Kampagne
 * gerade ausgespielt wird, ergibt sich daraus zusammen mit Laufzeit, Budget, Guthaben und Werbekonto
 * (kampagneWirksam) – so greifen Budget und Laufzeit, ohne dass ein Lauf den Status umschreiben muss.
 *
 * RM-9: vier Formate (Kampagnentyp), alle per CPC abgerechnet – Vorgabe docs/retail-media-formate.md.
 */

export const KAMPAGNE_TYPEN = {
  sponsored_product: 'Sponsored Product Ads',
  sponsored_brand: 'Sponsored Brand Ads',
  display: 'Display Ads',
  offsite: 'Offsite / Audience Extension',
} as const;
export type KampagneTyp = keyof typeof KAMPAGNE_TYPEN;
export const KAMPAGNE_TYP_LISTE = ['sponsored_product', 'sponsored_brand', 'display', 'offsite'] as const satisfies readonly KampagneTyp[];
/** Offsite ist vorerst ausgeblendet (Auftraggeber, 30.10.2026) – Code und Daten bleiben, true schaltet es wieder ein */
export const OFFSITE_AKTIV: boolean = false;
/** Formate, die sich anlegen und auswählen lassen */
export const KAMPAGNE_TYP_AKTIV: readonly KampagneTyp[] = KAMPAGNE_TYP_LISTE.filter((t) => t !== 'offsite' || OFFSITE_AKTIV);

/** Beschreibung eines Formats für die Oberfläche (Assistent, Formatwahl, Berichte) */
export interface KampagneFormat {
  name: string;
  kurztext: string;
  /** wo das Format erscheint, als Fließtext */
  platzierungenText: string;
  /** abgerechnet wird immer der Klick */
  abrechnung: 'CPC';
  kpis: string[];
  tipp: string | null;
  /** Art des Werbemittels – null: Produkte direkt in der Anzeigengruppe (anzeige_produkt) */
  werbemittel: WerbemittelArt | null;
  /** Standard-Platzierungen dieses Formats (Schlüssel aus PLATZIERUNG_STANDARD) */
  platzierungen: string[];
}

export const KAMPAGNE_FORMATE: Record<KampagneTyp, KampagneFormat> = {
  sponsored_product: {
    name: KAMPAGNE_TYPEN.sponsored_product,
    kurztext: `Native Produktanzeigen zwischen den organischen Treffern, gekennzeichnet als „${ANZEIGE_LABEL}“.`,
    platzierungenText: 'Suche, Kategorie, Produktdetailseite, Warenkorb',
    abrechnung: 'CPC',
    kpis: ['ROAS', 'Conversion Rate'],
    tipp: null,
    werbemittel: null,
    platzierungen: ['suche_treffer', 'kategorie_grid', 'produkt_detail', 'warenkorb'],
  },
  sponsored_brand: {
    name: KAMPAGNE_TYPEN.sponsored_brand,
    kurztext: 'Markenbanner mit Logo, Headline und bis zu drei Produkten über den Suchergebnissen.',
    platzierungenText: 'Suche, Kategorie, Markenseiten',
    abrechnung: 'CPC',
    kpis: ['Klickrate', 'Neukunden der Marke'],
    tipp: 'Stark bei Produktlaunches und generischen Suchbegriffen.',
    werbemittel: 'marke',
    platzierungen: ['suche_banner', 'kategorie_banner', 'markenseite'],
  },
  display: {
    name: KAMPAGNE_TYPEN.display,
    kurztext: 'Visuelle Banner mit Markenbotschaft.',
    platzierungenText: 'Hero Banner, In-Grid-Kacheln, Leaderboard, Produktdetailseite',
    abrechnung: 'CPC',
    kpis: ['Reichweite', 'Sichtbarkeit'],
    tipp: null,
    werbemittel: 'display',
    platzierungen: ['start_hero', 'kategorie_banner', 'grid_kachel', 'leaderboard', 'produkt_detail', 'checkout_bestaetigung'],
  },
  offsite: {
    name: KAMPAGNE_TYPEN.offsite,
    kurztext: 'Anzeigen außerhalb des Shops (Meta, Google, TikTok, Newsletter) – abgerechnet wird der Klick zurück in den Shop.',
    platzierungenText: 'Meta, Google, TikTok, Newsletter',
    abrechnung: 'CPC',
    kpis: ['Klicks', 'ROAS'],
    tipp: null,
    werbemittel: null,
    platzierungen: ['offsite'],
  },
};

/** Kanäle der Offsite-Weiterleitung (offsite_link.kanal) */
export const OFFSITE_KANAELE = { meta: 'Meta', google: 'Google', tiktok: 'TikTok', newsletter: 'Newsletter', sonstige: 'Sonstige' } as const;
export type OffsiteKanal = keyof typeof OFFSITE_KANAELE;

export const WKZ_EINHEITEN = { tag: 'Tag', woche: 'Woche', stueck: 'Stück' } as const;
export type WkzEinheit = keyof typeof WKZ_EINHEITEN;
/** Art einer WKZ-Fläche: im Shop (Ausspielung über das SDK) oder sonstige Leistung ohne Ausspielung (Newsletter, Social-Post …) */
export const WKZ_ARTEN = { shop: 'Im Shop', sonstige: 'Sonstige Leistung' } as const;
export type WkzArt = keyof typeof WKZ_ARTEN;

export const KAMPAGNE_STATUS = { entwurf: 'Entwurf', aktiv: 'Aktiv', pausiert: 'Pausiert', beendet: 'Beendet' } as const;
export type KampagneStatus = keyof typeof KAMPAGNE_STATUS;

/** Stand der Ausspielung, berechnet aus Status, Laufzeit, Budget, Guthaben und Werbekonto */
export const KAMPAGNE_WIRKSAM = {
  entwurf: 'Entwurf',
  geplant: 'Geplant',
  laeuft: 'Läuft',
  pausiert: 'Pausiert',
  budget_erschoepft: 'Budget erschöpft',
  tagesbudget_erschoepft: 'Tagesbudget erschöpft',
  kein_guthaben: 'Kein Guthaben',
  /** Abbuchung vom hinterlegten Zahlungsmittel fehlgeschlagen – pausiert, bis eine Zahlung klappt */
  zahlung_fehlgeschlagen: 'Abbuchung fehlgeschlagen',
  konto_gesperrt: 'Werbekonto gesperrt',
  abgelaufen: 'Abgelaufen',
  beendet: 'Beendet',
} as const;
export type KampagneWirksam = keyof typeof KAMPAGNE_WIRKSAM;

export const GEBOT_STRATEGIEN = { manuell: 'Festes Gebot', ziel_roas: 'Ziel-ROAS' } as const;
export type GebotStrategie = keyof typeof GEBOT_STRATEGIEN;

export const ANZEIGE_PRODUKT_STATUS = { aktiv: 'Aktiv', pausiert: 'Pausiert' } as const;
export type AnzeigeProduktStatus = keyof typeof ANZEIGE_PRODUKT_STATUS;

/** Standard-Platzierung: Formate, die dort bieten dürfen, und die WKZ-Vorgaben (Preis 0 = nicht buchbar) */
export interface PlatzierungStandard {
  schluessel: string;
  name: string;
  mindestgebotCent: number;
  formate: readonly KampagneTyp[];
  wkz: boolean;
  wkzPreisCent: number;
  wkzEinheit: WkzEinheit;
  wkzKapazitaet: number;
  wkzAuktionOhneBuchung: boolean;
}

const cpc = (schluessel: string, name: string, formate: readonly KampagneTyp[]): PlatzierungStandard => ({
  schluessel, name, mindestgebotCent: 10, formate, wkz: false, wkzPreisCent: 0, wkzEinheit: 'woche', wkzKapazitaet: 1, wkzAuktionOhneBuchung: true,
});
const wkz = (schluessel: string, name: string, formate: readonly KampagneTyp[], wkzEinheit: WkzEinheit, wkzKapazitaet: number): PlatzierungStandard => ({
  ...cpc(schluessel, name, formate), wkz: true, wkzEinheit, wkzKapazitaet,
});

/**
 * Standard-Platzierungen jedes Marktplatzes. platzierungenLaden() trägt fehlende Schlüssel nach; bestehende
 * Einstellungen (Name, Mindestgebot, aktiv, Formate, WKZ) bleiben unverändert.
 */
export const PLATZIERUNG_STANDARD: readonly PlatzierungStandard[] = [
  cpc('kategorie_grid', 'Kategorieseite', ['sponsored_product']),
  cpc('suche_treffer', 'Suchergebnisse', ['sponsored_product']),
  cpc('produkt_detail', 'Produktdetailseite', ['sponsored_product', 'display']),
  cpc('warenkorb', 'Warenkorb', ['sponsored_product']),
  cpc('suche_banner', 'Suche – Banner über den Treffern', ['sponsored_brand']),
  cpc('kategorie_banner', 'Kategorie – Banner', ['sponsored_brand', 'display']),
  cpc('markenseite', 'Markenseite', ['sponsored_brand']),
  cpc('grid_kachel', 'Kachel im Produktraster', ['display']),
  cpc('leaderboard', 'Leaderboard', ['display']),
  cpc('checkout_bestaetigung', 'Bestellbestätigung', ['display']),
  cpc('offsite', 'Offsite (außerhalb des Shops)', ['offsite']),
  // Startseite: per WKZ buchbar, an freien Tagen CPC-Auktion
  wkz('start_hero', 'Startseite – Hero Banner', ['display'], 'woche', 1),
  wkz('start_takeover', 'Startseite – Takeover', ['display'], 'tag', 1),
  wkz('start_kacheln', 'Startseite – Kacheln', ['display'], 'woche', 3),
];

export const KAMPAGNE_GRENZEN = {
  budgetMinCent: 1_000,
  budgetMaxCent: 100_000_000,
  tagesMinCent: 100,
  gebotMinCent: 1,
  gebotMaxCent: 10_000,
  gruppenJeKampagne: 20,
  produkteJeGruppe: 1_000,
  /** höchstens so viele Produkte je Aufruf hinzufügen */
  produkteJeAufruf: 200,
  /** Ausrichtung je Anzeigengruppe: Einträge und Zeichen je Eintrag */
  ausrichtungEintraege: 50,
  ausrichtungZeichen: 80,
} as const;

/** Keywords und Kategorien: getrimmt, klein, ohne Leere und Doppelte (Reihenfolge bleibt) */
export function ausrichtungNormalisieren(liste: readonly string[]): string[] {
  const aus: string[] = [];
  for (const roh of liste) {
    const w = roh.trim().replace(/\s+/g, ' ').toLowerCase();
    if (w && !aus.includes(w)) aus.push(w);
  }
  return aus;
}

/* ------------------------------------------------------------------ Formate in der Oberfläche */

/** Platzierungen, auf denen ein Format bieten darf: aktiv und `formate` enthält den Kampagnentyp */
export function platzierungenFuerFormat<P extends Pick<Platzierung, 'aktiv' | 'formate'>>(liste: readonly P[], typ: KampagneTyp): P[] {
  return liste.filter((p) => p.aktiv && p.formate.includes(typ));
}

/**
 * Vorauswahl im Assistenten: die Standard-Platzierungen des Formats, soweit der Marktplatz sie anbietet;
 * gibt es keine davon, die erste passende Platzierung.
 */
export function platzierungVorauswahl(liste: readonly Pick<Platzierung, 'schluessel' | 'aktiv' | 'formate'>[], typ: KampagneTyp): string[] {
  const passend = platzierungenFuerFormat(liste, typ).map((p) => p.schluessel);
  const standard = KAMPAGNE_FORMATE[typ].platzierungen.filter((s) => passend.includes(s));
  return standard.length ? standard : passend.slice(0, 1);
}

/** Welche Ausrichtung zu den gewählten Platzierungen passt: Suchbegriffe für suche_*, Kategorien für kategorie_* und grid_kachel */
export function ausrichtungFuer(platzierungen: readonly string[]): { keywords: boolean; kategorien: boolean } {
  return {
    keywords: platzierungen.some((p) => p.startsWith('suche_')),
    kategorien: platzierungen.some((p) => p.startsWith('kategorie_') || p === 'grid_kachel'),
  };
}

/** Eingabe für Ausrichtungs-Chips: Komma, Semikolon oder Zeilenumbruch trennen; normalisiert wie im Schema */
export function ausrichtungAusText(text: string, vorhanden: readonly string[] = []): string[] {
  return ausrichtungNormalisieren([...vorhanden, ...text.split(/[,;\n]/)]);
}

/* ------------------------------------------------------------------ Eingaben */

const Name = z.string().trim().min(2, 'Bitte Namen angeben').max(120, 'Höchstens 120 Zeichen');
const Cent = z.number({ invalid_type_error: 'Bitte Betrag angeben' }).int('Bitte Betrag in Cent angeben');
/** Datum JJJJ-MM-TT (Laufzeit gilt ganztägig in Europe/Berlin) */
const Datum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Bitte Datum angeben');

const KampagneFelder = z.object({
  name: Name,
  budgetCent: Cent.min(KAMPAGNE_GRENZEN.budgetMinCent, 'Mindestens 10 €').max(KAMPAGNE_GRENZEN.budgetMaxCent, 'Budget zu hoch'),
  /** null: kein Tagesbudget */
  tagesCent: Cent.min(KAMPAGNE_GRENZEN.tagesMinCent, 'Mindestens 1 €').nullable(),
  start: Datum,
  /** null: ohne Enddatum */
  ende: Datum.nullable(),
});

function laufzeitPruefen(k: { start: string; ende: string | null; budgetCent: number; tagesCent: number | null }, ctx: z.RefinementCtx) {
  if (k.ende && k.ende < k.start) ctx.addIssue({ code: 'custom', path: ['ende'], message: 'Ende liegt vor dem Start' });
  if (k.tagesCent !== null && k.tagesCent > k.budgetCent) ctx.addIssue({ code: 'custom', path: ['tagesCent'], message: 'Höher als das Gesamtbudget' });
}

const Platzierungen = z.array(z.string().regex(/^[a-z][a-z0-9_]{1,39}$/)).min(1, 'Bitte mindestens eine Platzierung wählen').max(20);

const Ausrichtung = z.array(z.string().max(500)).max(500).default([])
  .transform(ausrichtungNormalisieren)
  .pipe(z.array(z.string().max(KAMPAGNE_GRENZEN.ausrichtungZeichen, `Höchstens ${KAMPAGNE_GRENZEN.ausrichtungZeichen} Zeichen je Eintrag`))
    .max(KAMPAGNE_GRENZEN.ausrichtungEintraege, `Höchstens ${KAMPAGNE_GRENZEN.ausrichtungEintraege} Einträge`));

const GruppeFelder = z.object({
  name: Name,
  gebotCent: Cent.min(KAMPAGNE_GRENZEN.gebotMinCent, 'Bitte Gebot angeben').max(KAMPAGNE_GRENZEN.gebotMaxCent, 'Höchstens 100 € je Klick'),
  strategie: z.enum(['manuell', 'ziel_roas']).default('manuell'),
  /** nur bei Ziel-ROAS: Umsatz je Euro Werbekosten, z. B. 4 = 400 % */
  zielRoas: z.number().min(0.1, 'Mindestens 0,1').max(100, 'Höchstens 100').nullable().default(null),
  platzierungen: Platzierungen,
  /** Suchbegriffe (für suche_*) – leer = überall auf den gewählten Platzierungen */
  keywords: Ausrichtung,
  /** Kategorie- oder Kollektionsnamen (für kategorie_*, grid_kachel) – leer = überall */
  kategorien: Ausrichtung,
  /** Pflicht bei sponsored_brand und display; leer bei sponsored_product und offsite */
  werbemittelId: z.string().uuid().nullable().default(null),
});
function roasPruefen(g: { strategie: GebotStrategie; zielRoas: number | null }, ctx: z.RefinementCtx) {
  if (g.strategie === 'ziel_roas' && g.zielRoas === null) ctx.addIssue({ code: 'custom', path: ['zielRoas'], message: 'Bitte Ziel-ROAS angeben' });
}

export const AnzeigengruppeSchema = GruppeFelder.superRefine(roasPruefen);
export type AnzeigengruppeEingabe = z.infer<typeof AnzeigengruppeSchema>;

/** Kampagne anlegen – mit der ersten Anzeigengruppe und ihren Produkten in einem Schritt (Assistent) */
export const KampagneAnlegenSchema = KampagneFelder.extend({
  /** Format – lässt sich nach dem Anlegen nicht mehr ändern */
  typ: z.enum(KAMPAGNE_TYP_LISTE).default('sponsored_product'),
  gruppe: GruppeFelder.extend({
    produkte: z.array(z.string().uuid()).max(KAMPAGNE_GRENZEN.produkteJeAufruf, `Höchstens ${KAMPAGNE_GRENZEN.produkteJeAufruf} Produkte auf einmal`).default([]),
    /** E1: Produktgruppen des Werbekontos (zusätzlich zu einzelnen Produkten) */
    produktgruppen: z.array(z.string().uuid()).max(10, 'Höchstens 10 Produktgruppen').optional(),
  }).superRefine(roasPruefen),
}).superRefine(laufzeitPruefen);
export type KampagneAnlegen = z.infer<typeof KampagneAnlegenSchema>;

export const KampagneAendernSchema = KampagneFelder.superRefine(laufzeitPruefen);
export type KampagneAendern = z.infer<typeof KampagneAendernSchema>;

export const KampagneStatusSchema = z.object({ status: z.enum(['aktiv', 'pausiert', 'beendet']) });
export type KampagneStatusAendern = z.infer<typeof KampagneStatusSchema>;

/** Eingriff des Marktplatzes: pausieren oder beenden – aktivieren kann nur der Werbetreibende */
export const KontoKampagneStatusSchema = z.object({ status: z.enum(['pausiert', 'beendet']) });
export type KontoKampagneStatus = z.infer<typeof KontoKampagneStatusSchema>;

export const AnzeigeProdukteSchema = z.object({
  produkte: z.array(z.string().uuid()).min(1, 'Bitte Produkte auswählen').max(KAMPAGNE_GRENZEN.produkteJeAufruf),
});
export const AnzeigeProduktStatusSchema = z.object({ status: z.enum(['aktiv', 'pausiert']) });

export const KampagnenAbfrage = z.object({
  cursor: z.string().max(400).optional(),
  status: z.enum(['entwurf', 'aktiv', 'pausiert', 'beendet']).optional(),
  werbekonto: z.string().uuid().optional(),
  /** RM-9: nur Kampagnen dieses Formats */
  typ: z.enum(KAMPAGNE_TYP_LISTE).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const PlatzierungSchema = z.object({
  name: z.string().trim().min(2, 'Bitte Namen angeben').max(80),
  mindestgebotCent: Cent.min(1, 'Mindestens 0,01 €').max(KAMPAGNE_GRENZEN.gebotMaxCent),
  aktiv: z.boolean(),
  /* RM-9 – fehlt ein Feld, bleibt der gespeicherte Wert (beim Anlegen: Standard der Spalte) */
  formate: z.array(z.enum(KAMPAGNE_TYP_LISTE)).min(1, 'Bitte mindestens ein Format wählen').max(KAMPAGNE_TYP_LISTE.length)
    .transform((f) => [...new Set(f)]).optional(),
  wkz: z.boolean().optional(),
  wkzPreisCent: Cent.min(0, 'Nicht negativ').max(100_000_000, 'Preis zu hoch').optional(),
  wkzEinheit: z.enum(['tag', 'woche', 'stueck']).optional(),
  wkzKapazitaet: z.number().int().min(1, 'Mindestens 1').max(20, 'Höchstens 20').optional(),
  wkzAuktionOhneBuchung: z.boolean().optional(),
});
export const PlatzierungAnlegenSchema = PlatzierungSchema.extend({
  schluessel: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'Kleinbuchstaben, Ziffern und _ (2–40 Zeichen)'),
});
export type PlatzierungEingabe = z.infer<typeof PlatzierungSchema>;
export type PlatzierungAnlegen = z.infer<typeof PlatzierungAnlegenSchema>;

/* ------------------------------------------------------------------ Antworten */

export interface Platzierung {
  id: string;
  schluessel: string;
  name: string;
  mindestgebotCent: number;
  aktiv: boolean;
  /** Kampagnentypen, die hier bieten dürfen */
  formate: KampagneTyp[];
  /** per WKZ (Festpreis je Zeitraum) buchbar */
  wkz: boolean;
  /** Preis je Einheit; 0 = nicht buchbar, bis der Marktplatz einen Preis setzt */
  wkzPreisCent: number;
  wkzEinheit: WkzEinheit;
  /** gleichzeitige Buchungen je Tag (1 = exklusiv) */
  wkzKapazitaet: number;
  /** an freien Tagen dürfen CPC-Kampagnen ausspielen */
  wkzAuktionOhneBuchung: boolean;
  /** WKZ-Fläche im Shop oder sonstige Leistung ohne Ausspielung (Migration 0590) */
  wkzArt: WkzArt;
  wkzBeschreibung: string;
  /** Vorgaben für das Werbemittel */
  wkzMasse: string;
  wkzHinweise: string;
  /** frühester Start: heute + Vorlauf (1 = morgen) */
  wkzVorlaufTage: number;
}

export interface KampagneZeile {
  id: string;
  werbekontoId: string;
  werbekonto: string;
  name: string;
  typ: KampagneTyp;
  status: KampagneStatus;
  wirksam: KampagneWirksam;
  budgetCent: number;
  tagesCent: number | null;
  ausgegebenCent: number;
  start: string;
  ende: string | null;
  gruppen: number;
  produkte: number;
  erstelltAm: string;
}

export interface KampagneSeite {
  eintraege: KampagneZeile[];
  weiter: string | null;
}

export interface AnzeigeProdukt {
  id: string;
  produktId: string;
  titel: string;
  bild: string | null;
  /** Produkt steht im Shop (freigegeben/gelistet) – sonst wird es nicht ausgespielt */
  ausspielbar: boolean;
  produktStatus: string;
  status: AnzeigeProduktStatus;
  /** E1: warum das Produkt gerade nicht ausgespielt wird (ausverkauft …) – null: bewerbbar */
  grund: NichtBewerbbarGrund | null;
}

/** E1: Produktgruppe, auf die eine Anzeigengruppe verweist */
export interface AnzeigengruppeProduktgruppe {
  id: string;
  name: string;
  art: ProduktgruppeArt;
  anzahlGesamt: number;
  anzahlBewerbbar: number;
}

export interface Anzeigengruppe {
  id: string;
  name: string;
  gebotCent: number;
  strategie: GebotStrategie;
  zielRoas: number | null;
  platzierungen: string[];
  keywords: string[];
  kategorien: string[];
  werbemittelId: string | null;
  werbemittel: WerbemittelKurz | null;
  produkte: AnzeigeProdukt[];
  produktgruppen: AnzeigengruppeProduktgruppe[];
}

export interface KampagneDetail extends KampagneZeile {
  anzeigengruppen: Anzeigengruppe[];
  /** Hinweise, warum eine aktive Kampagne nicht läuft oder was vor dem Aktivieren fehlt */
  hinweise: string[];
}

/** Antwort auf „Produkte hinzufügen“: wie viele neu dazukamen (bereits enthaltene zählen nicht) und der neue Stand */
export interface AnzeigeProdukteErgebnis {
  neu: number;
  kampagne: KampagneDetail;
}
